import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/admin/_layout/organization/fiscal-years"
)({
  component: RouteComponent,
});

function RouteComponent() {
  return <FiscalYearDataTable />;
}

import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";

import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { useDebounce } from "@/hooks/use-debounce";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import useCreateFiscalYear from "@/hooks/api/organization/fiscal-year/useCreateFiscalYear";
import useUpdateFiscalYear from "@/hooks/api/organization/fiscal-year/useUpdateFiscalYear";
import useDeleteFiscalYear from "@/hooks/api/organization/fiscal-year/useDeleteFiscalYear";
import useFetchFiscalYears, {
  type FiscalYearOrdering,
} from "@/hooks/api/organization/fiscal-year/useFetchFiscalYear";
import type {
  FiscalYear,
  FiscalYearPayload,
  FiscalYearType,
} from "@/types/organization/fiscal-year";
import { FiscalYearTable } from "@/components/crud/table/FiscalYearTable";
import { FiscalYearFormSheet } from "@/components/crud/forms/FiscalYearFormSheet";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function FiscalYearDataTable() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "organization.setting.manage");
  // UI State
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingFiscalYear, setEditingFiscalYear] = useState<FiscalYear | null>(
    null
  );
  const [fiscalYearToDelete, setFiscalYearToDelete] =
    useState<FiscalYear | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        from_date: SortDirection;
        to_date: SortDirection;
      },
      FiscalYearOrdering
    >({
      name: null,
      from_date: null,
      to_date: null,
    });

  const [activeFilter, setActiveFilter] = useState<
    "true" | "false" | undefined
  >("true");
  const [fyTypeFilter, setFyTypeFilter] = useState<
    FiscalYearType | undefined
  >();

  // Global Search Filter
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(globalFilter, 500);

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // Fiscal Year Crud Hooks
  const fiscalYearsQuery = useFetchFiscalYears({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    is_active:
      activeFilter === "true"
        ? true
        : activeFilter === "false"
          ? false
          : undefined,
    fy_type: fyTypeFilter,
  });
  const fiscalYears = fiscalYearsQuery.data?.results ?? [];
  const count = fiscalYearsQuery.data?.count ?? 0;

  const createFiscalYear = useCreateFiscalYear();
  const updateFiscalYear = useUpdateFiscalYear();
  const deleteFiscalYear = useDeleteFiscalYear();

  const isMutating =
    createFiscalYear.isPending ||
    updateFiscalYear.isPending ||
    deleteFiscalYear.isPending;
  const isLoading = fiscalYearsQuery.isPending || isMutating;
  const isFetching = fiscalYearsQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, activeFilter, fyTypeFilter, setPage]);

  // For resetting filters
  const resetFilters = () => {
    setOrdering({
      name: null,
      from_date: null,
      to_date: null,
    });
    setActiveFilter("true");
    setFyTypeFilter(undefined);
    setGlobalFilter("");
    resetDebounce();
    setPage(1);
  };

  // Handlers
  const handleCreate = () => {
    setEditingFiscalYear(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (fiscalYear: FiscalYear) => {
    setEditingFiscalYear(fiscalYear);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleView = (fiscalYear: FiscalYear) => {
    setEditingFiscalYear(fiscalYear);
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (fiscalYear: FiscalYear) => {
    setFiscalYearToDelete(fiscalYear);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fiscalYearToDelete) return;
    try {
      await deleteFiscalYear.mutateAsync({ id: fiscalYearToDelete.id });
      toast.success("Fiscal year deleted successfully!");
      setIsDeleteDialogOpen(false);
      setFiscalYearToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (data: FiscalYearPayload) => {
    if (editingFiscalYear) {
      await updateFiscalYear.mutateAsync({
        id: editingFiscalYear.id,
        payload: data,
      });
      toast.success("Fiscal year updated successfully!");
    } else {
      await createFiscalYear.mutateAsync(data);
      toast.success("Fiscal year created successfully!");
    }

    setIsSheetOpen(false);
  };

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const [showFilters, setShowFilters] = useState(false);

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Fiscal Years"
          description="Manage organization fiscal years here."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Create Fiscal Year"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search fiscal years..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-8 max-w-sm font-light"
            />
          </div>
          {/* Active Status Filter */}
          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Active", value: "true" },
              { label: "Inactive", value: "false" },
            ]}
            value={activeFilter}
            onChange={setActiveFilter}
          />
          {/* Fiscal Year Type Filter */}
          <SelectFilter
            placeholder="Type"
            options={[
              { label: "Leave", value: "LEAVE" },
              { label: "Global", value: "GLOBAL" },
            ]}
            value={fyTypeFilter}
            onChange={setFyTypeFilter}
          />
        </FiltersPanel>

        {/* Fiscal Years Table */}
        <FiscalYearTable
          fiscalYears={fiscalYears}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        {/* Fiscal Year Form Sheet */}
        <FiscalYearFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          editingFiscalYear={editingFiscalYear}
          isViewOnly={isViewOnly}
          isLoading={isMutating}
        />

        {/* Delete Confirmation */}
        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Fiscal Year"
          description={`Are you sure you want to delete fiscal year "${fiscalYearToDelete?.name}"? This action cannot be undone.`}
          itemName={fiscalYearToDelete?.name}
          isLoading={deleteFiscalYear.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
